import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Ban, CircleCheck, Eye, History, Plus, Search } from "lucide-react";
import toast from "react-hot-toast";
import { DataTable, Column } from "../../../DataTable/DataTable";
import { ProductCatalogService } from "../../../../services/api/productCatalog.service";
import { InverntroyService } from "../../../../services/api/inventroy.service";
import { useAuth } from "../../../../context/AuthContext";
import { FilterSelect } from "./MasterPanel";
import { ProductVerificationHistoryModal } from "./ProductVerificationHistoryModal";
import { SearchableDropdown } from "../../../../shared/SearchableDropdown";

interface ProductListingRow {
  id: string;
  productId: string;
  productName: string;
  description: string | null;
  isActive: boolean | null;
  isPublished: boolean;
}

interface ProductListingTabProps {
  /** Opens the product form; no id means create. */
  onOpenProduct: (productId: string | null) => void;
  /** Bumped by the parent after the form saves, to re-fetch the page. */
  refreshToken?: number;
}

const TRISTATE_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const toBool = (value: string): boolean | undefined =>
  value === "" ? undefined : value === "true";

const truncate = (text: string | null, words = 24) => {
  if (!text) return "-";
  const parts = text.split(" ");
  return parts.length <= words ? text : `${parts.slice(0, words).join(" ")}...`;
};

/**
 * Admin product listing.
 *
 * Reads the V3 admin listing, whose `search` is a trigram match - a typo or a
 * partial brand still finds the product, closest name first. No filter defaults
 * to true here, so inactive and unpublished products are visible until a filter
 * is set.
 */
export function ProductListingTab({
  onOpenProduct,
  refreshToken = 0,
}: ProductListingTabProps) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [rows, setRows] = useState<ProductListingRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [activeFilter, setActiveFilter] = useState("");
  const [publishedFilter, setPublishedFilter] = useState("");
  const [rxFilter, setRxFilter] = useState("");
  const [scheduleFilter, setScheduleFilter] = useState("");
  const [marketerFilter, setMarketerFilter] = useState("");
  const [marketerSearchText, setMarketerSearchText] = useState("");
  const [showMarketerSuggestions, setShowMarketerSuggestions] = useState(false);
  const marketerDropdownRef = useRef<HTMLDivElement>(null);

  const [scheduleOptions, setScheduleOptions] = useState<
    { id: number; name: string }[]
  >([]);
  const [marketerOptions, setMarketerOptions] = useState<
    { id: string; marketerName: string }[]
  >([]);

  const [historyProductId, setHistoryProductId] = useState<string | null>(null);

  const selectedMarketerName = marketerOptions.find(
    (option) => option.id === marketerFilter
  )?.marketerName;

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchText), 600);
    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      const [schedules, marketers] = await Promise.all([
        ProductCatalogService.getScheduleOptions({ isActive: true }),
        InverntroyService.getMarketerOptions(),
      ]);
      if (cancelled) return;
      setScheduleOptions(schedules?.success ? schedules.data || [] : []);
      // getMarketerOptions unwraps the envelope itself and hands back a bare array.
      setMarketerOptions(Array.isArray(marketers) ? marketers : []);
    };
    loadOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        marketerDropdownRef.current &&
        !marketerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowMarketerSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearch,
    activeFilter,
    publishedFilter,
    rxFilter,
    scheduleFilter,
    marketerFilter,
  ]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await ProductCatalogService.getAdminProductListing({
        page: currentPage,
        pageSize,
        search: debouncedSearch || undefined,
        isActive: toBool(activeFilter),
        isPublished: toBool(publishedFilter),
        isRxRequired: toBool(rxFilter),
        scheduleMedicineId: scheduleFilter ? Number(scheduleFilter) : undefined,
        marketerId: marketerFilter || undefined,
      });
      if (res?.success) {
        setRows(
          (res.data || []).map((row) => ({ ...row, id: row.productId }))
        );
        setTotalPages(res.totalPages ?? 1);
        setTotalItems(res.totalItems ?? 0);
      } else {
        setRows([]);
        setTotalPages(1);
        setTotalItems(0);
        if (res?.message) toast.error(res.message);
      }
    } catch (error) {
      toast.error(`${error}`);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    debouncedSearch,
    activeFilter,
    publishedFilter,
    rxFilter,
    scheduleFilter,
    marketerFilter,
  ]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  const toggleActive = async (row: ProductListingRow) => {
    const nextStatus = !row.isActive;
    const res = await ProductCatalogService.setProductActive(
      row.productId,
      nextStatus
    );
    if (res?.success) {
      toast.success(
        `Product ${nextStatus ? "activated" : "deactivated"} successfully`
      );
      load();
    } else {
      toast.error(res?.message || "Failed to update status");
    }
  };

  const columns: Column<ProductListingRow>[] = useMemo(
    () => [
      {
        key: "productName",
        title: "Product",
        width: "w-[280px]",
        render: (row) => (
          <div
            className="text-sm font-medium text-gray-800 line-clamp-2"
            title={row.productName}
          >
            {row.productName}
          </div>
        ),
      },
      {
        key: "description",
        title: "Description",
        width: "w-[320px]",
        render: (row) => (
          <div className="text-sm text-gray-600" title={row.description || ""}>
            {truncate(row.description)}
          </div>
        ),
      },
      {
        key: "isPublished",
        title: "Published",
        width: "w-[110px]",
        render: (row) =>
          row.isPublished ? (
            <CircleCheck className="w-5 h-5 bg-[#199C4D] rounded-full text-white" />
          ) : (
            <Ban className="w-5 h-5 bg-[#c62a2a] rounded-full text-white" />
          ),
      },
      {
        key: "isActive",
        title: "Active",
        width: "w-[100px]",
        render: (row) => (
          <button
            type="button"
            onClick={() => toggleActive(row)}
            title={row.isActive ? "Deactivate product" : "Activate product"}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              row.isActive ? "bg-emerald-500" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                row.isActive ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
        ),
      },
      {
        key: "actions",
        title: "Actions",
        width: "w-[120px]",
        render: (row) => (
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => onOpenProduct(row.productId)}
              className="text-blue-500 hover:text-blue-700"
              title="View / Edit product"
            >
              <Eye className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setHistoryProductId(row.productId)}
              className="text-gray-400 hover:text-gray-600"
              title="Verification history"
            >
              <History className="w-5 h-5" />
            </button>
          </div>
        ),
      },
    ],
    // `load` is stable for the lifetime of a filter set, and toggleActive calls it.
    [load, onOpenProduct]
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <FilterSelect
            label="Active"
            value={activeFilter}
            onChange={setActiveFilter}
            options={TRISTATE_OPTIONS}
            width="w-28"
          />
          <FilterSelect
            label="Published"
            value={publishedFilter}
            onChange={setPublishedFilter}
            options={TRISTATE_OPTIONS}
            width="w-28"
          />
          <FilterSelect
            label="Rx Required"
            value={rxFilter}
            onChange={setRxFilter}
            options={TRISTATE_OPTIONS}
            width="w-28"
          />
          <FilterSelect
            label="Schedule"
            value={scheduleFilter}
            onChange={setScheduleFilter}
            options={[
              { value: "", label: "All" },
              ...scheduleOptions.map((option) => ({
                value: String(option.id),
                label: option.name,
              })),
            ]}
          />
          {/* Marketers run into the hundreds, so this is typed, not scrolled. */}
          <div className="w-56">
            <label className="block text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-1">
              Marketer
            </label>
            <SearchableDropdown
              placeholder="Type a marketer name"
              searchText={marketerSearchText}
              setSearchText={(text) => {
                setMarketerSearchText(text);
                // Emptying the box drops the filter, so the list can never stay
                // filtered by a name that is no longer on screen.
                if (!text.trim()) setMarketerFilter("");
              }}
              suggestionsVisible={showMarketerSuggestions}
              setSuggestionsVisible={setShowMarketerSuggestions}
              options={marketerOptions.map((option) => ({
                id: option.id,
                label: option.marketerName,
              }))}
              onSelect={(selected) => {
                setMarketerFilter(selected.id);
                setMarketerSearchText(selected.label);
              }}
              dropdownRef={marketerDropdownRef}
            />
            {/* The box can hold text that matches nothing, so name the marketer
                the list is actually filtered by. */}
            {marketerFilter && selectedMarketerName !== marketerSearchText.trim() && (
              <p className="text-xs text-emerald-600 mt-1">
                Using: {selectedMarketerName || marketerFilter}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-end gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search products..."
              className="w-64 pl-10 pr-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          {isAdmin && (
            <button
              type="button"
              onClick={() => onOpenProduct(null)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </button>
          )}
        </div>
      </div>

      {totalItems > 0 && (
        <p className="text-xs text-gray-500">
          {totalItems} product{totalItems === 1 ? "" : "s"}
          {debouncedSearch ? " matching your search" : ""}
        </p>
      )}

      <DataTable
        data={rows}
        columns={columns}
        isLoading={isLoading}
        itemsPerPage={pageSize}
        customFilters={null}
        filterComponent={null}
        pagination={{
          currentPage,
          setCurrentPage,
          totalPages,
          setTotalPages,
          totalItemsCount: totalItems,
          pageSize,
          setPageSize,
        }}
      />

      {historyProductId && (
        <ProductVerificationHistoryModal
          productId={historyProductId}
          onClose={() => setHistoryProductId(null)}
        />
      )}
    </div>
  );
}
